import React, { useEffect, useState, useCallback } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Loader2, UserPlus, RefreshCw, Copy, Trash2, Search, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { inquilinosApi } from '../../../lib/api';

const ESTADO_BADGE = {
  sin_acceso:  { label: 'Sin acceso',  cls: 'bg-slate-100 text-slate-600' },
  pendiente:   { label: 'Pendiente',   cls: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200' },
  activo:      { label: 'Activo',      cls: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' },
  desactivado: { label: 'Desactivado', cls: 'bg-red-100 text-red-700 ring-1 ring-red-200' },
};

// Devuelve nombre a mostrar + tooltip completo según tipo_inquilino
const getInquilinoDisplay = (it) => {
  const isHumana = (it.tipo_inquilino || 'juridica') === 'humana';
  if (isHumana) {
    const name = it.firmante_nombre || it.razon_social || it.inquilino_contractual || '—';
    return { display: name, full: name };
  }
  const display = it.nombre_comercial || it.razon_social || it.inquilino_contractual || '—';
  const full = it.razon_social || it.nombre_comercial || it.inquilino_contractual || display;
  return { display, full };
};

export const TabInquilinos = ({ edificioId, activeTab }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [createDialog, setCreateDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null); // {item}
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!edificioId) return;
    setLoading(true);
    try {
      const res = await inquilinosApi.listByEdificio(edificioId);
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error('Error al cargar inquilinos');
    } finally { setLoading(false); }
  }, [edificioId]);

  useEffect(() => { if (activeTab === 'inquilinos') load(); }, [activeTab, load]);

  const handleCopyMensaje = async () => {
    try {
      await navigator.clipboard.writeText(inquilinosApi.getActivationMessage());
      toast.success('Mensaje de activación copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar el mensaje');
    }
  };

  const openCreate = (item) => {
    setCreateDialog(item);
    const inq = getInquilinoDisplay(item);
    setForm({
      nombre: inq.display !== '—' ? inq.display : '',
      email: '',
      telefono: '',
    });
  };

  const handleCreate = async () => {
    if (!createDialog) return;
    if (!form.email || !form.email.includes('@')) {
      toast.error('Email válido es obligatorio');
      return;
    }
    setSaving(true);
    try {
      await inquilinosApi.crearAcceso(createDialog.contrato_id, form);
      toast.success('Acceso creado. Compartile el link al inquilino.');
      setCreateDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear acceso');
    } finally { setSaving(false); }
  };

  const openEdit = (item) => {
    setEditDialog(item);
    setForm({
      nombre: item.acceso?.nombre || '',
      email: item.acceso?.email || '',
      telefono: item.acceso?.telefono || '',
    });
  };

  const handleEdit = async () => {
    if (!editDialog) return;
    const estado = editDialog.acceso?.estado;
    if (estado === 'activo' && form.email !== (editDialog.acceso?.email || '')) {
      toast.error('Para cambiar el email tenés que resetear el acceso primero.');
      return;
    }
    setSaving(true);
    try {
      await inquilinosApi.editarAcceso(editDialog.contrato_id, {
        nombre: form.nombre || null,
        email: form.email || null,
        telefono: form.telefono || null,
      });
      toast.success('Datos de acceso actualizados');
      setEditDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    } finally { setSaving(false); }
  };

  const handleReset = async (contratoId) => {
    if (!confirm('Resetear acceso del inquilino? Se invalidará la contraseña actual y el inquilino deberá activar nuevamente con su email.')) return;
    try {
      await inquilinosApi.resetAcceso(contratoId);
      toast.success('Acceso reseteado. El inquilino debe volver a activar con su email.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al resetear');
    }
  };

  const handleDeshabilitar = async (contratoId) => {
    if (!confirm('Desactivar acceso? El inquilino no podrá ingresar. Los datos se preservan.')) return;
    try {
      await inquilinosApi.deshabilitarAcceso(contratoId);
      toast.success('Acceso desactivado');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al desactivar');
    }
  };

  const filtered = items.filter(it => {
    if (!search) return true;
    const s = search.toLowerCase();
    const inq = getInquilinoDisplay(it);
    return (
      (it.unidad_nombre || '').toLowerCase().includes(s) ||
      inq.full.toLowerCase().includes(s) ||
      inq.display.toLowerCase().includes(s) ||
      (it.acceso?.email || '').toLowerCase().includes(s)
    );
  });

  return (
    <TabsContent value="inquilinos" className="mt-4" data-testid="tab-inquilinos-content">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Inquilinos — acceso a la plataforma</h2>
          <p className="text-xs text-slate-500">El link de activación es único para todos. El inquilino se identifica con su email.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar unidad, inquilino o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-9"
              data-testid="inquilinos-search"
            />
          </div>
          <Button
            onClick={handleCopyMensaje}
            className="h-9 bg-emerald-600 hover:bg-emerald-700"
            data-testid="inq-copy-mensaje"
            title="Copia un mensaje listo para enviar por WhatsApp/email"
          >
            <Copy className="w-4 h-4 mr-1.5" />
            Copiar mensaje de activación
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <p className="text-slate-500">No hay contratos vigentes con inquilinos para gestionar.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm min-h-full">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3">Unidad</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3">Inquilino</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3">Email acceso</th>
                <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3">Estado</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => {
                const e = it.acceso?.estado || 'sin_acceso';
                const meta = ESTADO_BADGE[e] || ESTADO_BADGE.sin_acceso;
                const inq = getInquilinoDisplay(it);
                return (
                  <tr key={it.contrato_id} className="border-b border-slate-100 hover:bg-slate-50 text-[13px]" data-testid={`inquilino-row-${it.contrato_id}`}>
                    <td className="py-2 px-3 whitespace-nowrap font-semibold text-slate-900" title={it.unidad_nombre || ''}>
                      {it.unidad_nombre || '—'}
                    </td>
                    <td className="py-2 px-3 max-w-[260px]">
                      <div className="truncate text-slate-800" title={inq.full}>{inq.display}</div>
                    </td>
                    <td className="py-2 px-3 max-w-[220px]">
                      <div className="truncate text-slate-700" title={it.acceso?.email || ''}>{it.acceso?.email || '—'}</div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-end gap-1">
                        {e === 'sin_acceso' ? (
                          <Button size="sm" onClick={() => openCreate(it)} className="h-7 text-[11px] px-2" data-testid={`inq-crear-${it.contrato_id}`}>
                            <UserPlus className="w-3 h-3 mr-1" /> Crear acceso
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="h-7 px-1.5" title="Editar datos de acceso" data-testid={`inq-editar-${it.contrato_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {(e === 'pendiente' || e === 'activo' || e === 'desactivado') && (
                          <Button size="sm" variant="outline" onClick={() => handleReset(it.contrato_id)} className="h-7 px-1.5" title="Resetear acceso" data-testid={`inq-reset-${it.contrato_id}`}>
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                        {(e === 'pendiente' || e === 'activo') && (
                          <Button size="sm" variant="outline" onClick={() => handleDeshabilitar(it.contrato_id)} className="h-7 px-1.5 text-red-600 hover:bg-red-50" title="Desactivar acceso" data-testid={`inq-desactivar-${it.contrato_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog: Crear acceso */}
      <Dialog open={!!createDialog} onOpenChange={(o) => !o && setCreateDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear acceso de inquilino</DialogTitle>
            <DialogDescription>
              Unidad <strong>{createDialog?.unidad_nombre}</strong>.
              <br />Inquilino contractual: <em>{createDialog ? getInquilinoDisplay(createDialog).full : ''}</em>
              <br />Una vez creado, copiá el mensaje de activación desde el botón superior y enviáselo al inquilino. Activará su cuenta con el link general usando el email que cargues acá.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Nombre y apellido</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Juan Pérez" data-testid="inq-form-nombre" />
              <p className="text-[11px] text-slate-500 mt-0.5">Puede ser distinto de la razón social del contrato.</p>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="juan@email.com" data-testid="inq-form-email" />
              <p className="text-[11px] text-slate-500 mt-0.5">Con este email el inquilino activará y luego ingresará al portal.</p>
            </div>
            <div>
              <Label className="text-xs">Teléfono / WhatsApp</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+54 261 ..." data-testid="inq-form-telefono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving || !form.email} data-testid="inq-form-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear acceso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar datos de acceso */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar datos de acceso</DialogTitle>
            <DialogDescription>
              Unidad <strong>{editDialog?.unidad_nombre}</strong> — estado <strong>{ESTADO_BADGE[editDialog?.acceso?.estado || 'sin_acceso']?.label}</strong>.
              {editDialog?.acceso?.estado === 'activo' && (
                <span className="block mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-[12px]">
                  El acceso ya está activo. Para cambiar el email tenés que resetear primero el acceso.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Nombre y apellido</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="inq-edit-nombre" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={editDialog?.acceso?.estado === 'activo'}
                data-testid="inq-edit-email"
              />
              {editDialog?.acceso?.estado === 'pendiente' && (
                <p className="text-[11px] text-slate-500 mt-0.5">Como aún no se activó, podés corregir el email.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Teléfono / WhatsApp</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="inq-edit-telefono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={saving} data-testid="inq-edit-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};

export default TabInquilinos;
